import { ApiChartTemplate, ApiChartType } from "@incendium/api";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { ChartBuilder } from "features/chartLibrary";
import { ChartLibraryList } from "features/chartLibrary";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { useSelectedProject } from "Hooks";
import { useCharts } from "Hooks/useCharts";
import { GenericDialoagProps, IChart } from "Interfaces";
import {
  defaultChart,
  useReportBuilderContext,
} from "Providers/ReportBuilderProvider";
import { useCallback, useEffect, useState } from "react";
import { friendlyDateTime } from "Helpers/dates";
import { TOP_BAR_HEIGHT } from "features/leftMenu/constants";

interface IChartLibraryDialogProps extends GenericDialoagProps {
  onSelect: (c: IChart) => void;
  hideTabs?: boolean;
  template?: ApiChartTemplate;
  type?: ApiChartType;
}

enum View {
  pick = "pick",
  build = "build",
}

function ChartLibraryDialog({
  open,
  setOpen,
  onSelect,
  hideTabs,
  template,
  type,
}: IChartLibraryDialogProps) {
  const [view, setView] = useState<View>(View.pick);
  const { chart, setChart } = useReportBuilderContext();
  const { saveNewChart } = useCharts();
  const { selectedProject } = useSelectedProject();
  const theme = useTheme();

  const onNewChart = useCallback(async () => {
    setChart({
      name: `Chart (${friendlyDateTime(new Date())})`,
      type: type || ApiChartType.TABLE,
      template: template || ApiChartTemplate.TABLE,
      yAxisKeys: [
        {
          key: "l",
          fields: [""],
        },
      ],
      dimension: [],
      attributes: [],
      orderBy: "",
    });
    setView(View.build);
  }, [setChart, saveNewChart, selectedProject, type, template]);

  const saveChart = useCallback(async () => {
    const res = await saveNewChart(chart, selectedProject);
    onSelect(res);
  }, [chart, saveNewChart, selectedProject, onSelect]);

  const cancel = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setChart(defaultChart);
      setView(View.pick);
    }
  }, [open, setChart]);

  return (
    <CustomDialog
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="xl"
      fullScreen={view === View.build}
      open={open}
      showClose={view !== View.build}
    >
      {view !== View.build && <DialogTitle>Pick a chart</DialogTitle>}

      <>
        {view === View.pick ? (
          <DialogContent
            sx={{
              background: theme.palette.background.default,
            }}
          >
            <Box mt={2} mb={4}>
              <ChartLibraryList
                onClick={onSelect}
                onCreate={onNewChart}
                selectOnly
                hideTabs={hideTabs}
                template={template}
                type={type}
              />
            </Box>
          </DialogContent>
        ) : (
          <Box
            sx={{
              background: theme.palette.background.default,
              height: `calc(100vh - ${TOP_BAR_HEIGHT}px - 70px)`,
              flex: 1,
              width: "100%",
            }}
          >
            <ChartBuilder />
          </Box>
        )}
      </>

      <DialogActions>
        <Button onClick={cancel} color="secondary">
          Cancel
        </Button>

        {view === View.build && (
          <Button onClick={saveChart}>Save and Add Chart</Button>
        )}
      </DialogActions>
    </CustomDialog>
  );
}

export default ChartLibraryDialog;
